import { Link } from "react-router-dom";
import MarketStats from "@/components/MarketStats";
import { ShieldCheck, Coins, Gavel, Handshake, ArrowRight, Search } from "lucide-react";
import Logo from "@/components/Logo";
import { t } from "@/lib/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const HERO = "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const STEPS = [
  { icon: ShieldCheck, t: "Register & get verified", d: "Upload company registration, tax and fleet papers. An admin approves every account." },
  { icon: Coins, t: "Buy credits", d: "1 credit = $10 = 1 load post or 1 bid. Same price for shippers and transporters." },
  { icon: Gavel, t: "Post or bid", d: "Shippers post loads at a rate per metric ton. Transporters accept the rate or counter-bid." },
  { icon: Handshake, t: "Connect", d: "Once both sides confirm, contact details and papers unlock. Plan the load offline." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <header className="absolute top-0 inset-x-0 z-20 flex items-center justify-between px-4 sm:px-6 md:px-12 h-24 gap-2">
        <div className="flex items-center gap-2 text-white">
          <Logo light className="text-2xl sm:text-3xl" tagline />
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <LanguageSwitcher light compact />
          <Link to="/login" className="hf-btn text-white hover:bg-white/10 px-3 sm:px-6" data-testid="landing-login-btn">{t("Sign in")}</Link>
          <Link to="/register" className="hf-btn-primary px-4 sm:px-6" data-testid="landing-register-btn">{t("Register")}</Link>
        </div>
      </header>

      <section className="relative min-h-[88vh] flex items-end bg-slate-900 overflow-hidden">
        <img src={HERO} alt="Freight truck on highway" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/60 to-transparent" />
        <div className="relative z-10 px-6 md:px-12 pb-16 md:pb-24 max-w-4xl hf-rise">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-5">{t("Where shippers and transporters connect")}</p>
          <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            {t("Bulk loads meet verified trucks.")}<br /><span className="text-amber-400">{t("No middlemen.")}</span>
          </h1>
          <p className="mt-6 text-slate-300 text-base md:text-lg max-w-xl">
            {t("Shippers post commodity loads priced per metric ton. Verified transporters bid on the loads that fit their fleet. We bring you together — you finalise the rest.")}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/register?role=shipper" className="hf-btn-primary h-12 px-6" data-testid="hero-shipper-cta">{t("I ship commodities")} <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/register?role=transporter" className="hf-btn h-12 px-6 bg-white text-slate-900 hover:bg-slate-100" data-testid="hero-transporter-cta">{t("I run trucks")} <Search className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      <MarketStats />

      <section className="px-6 md:px-12 py-20 md:py-28 hf-grid-bg">
        <div className="max-w-6xl">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-amber-700">{t("How it works")}</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl mt-3 max-w-xl">{t("Four steps from posting to handshake.")}</h2>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((s, i) => (
              <div key={s.t} className="hf-card p-6 hf-rise" style={{ animationDelay: `${i * 80}ms` }}>
                <p className="font-mono text-xs text-slate-400">0{i + 1}</p>
                <s.icon className="w-6 h-6 text-amber-600 mt-4" />
                <h3 className="font-display font-bold text-lg mt-4">{t(s.t)}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{t(s.d)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 py-16 bg-slate-900 text-white flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h2 className="font-display font-black text-2xl sm:text-3xl">{t("$10 per credit. That's it.")}</h2>
          <p className="text-slate-400 mt-2">{t("One credit posts a load or places a bid. No subscriptions.")}</p>
        </div>
        <Link to="/register" className="hf-btn-primary h-12 px-6 self-start" data-testid="footer-register-btn">{t("Create account")} <ArrowRight className="w-4 h-4" /></Link>
      </section>
      <footer className="px-6 md:px-12 py-6 bg-slate-950 text-slate-500 text-xs flex flex-wrap justify-between gap-3">
        <span>© {new Date().getFullYear()} {t("Mi-Load. Introductions only — load planning is finalised between parties.")}</span>
        <Link to="/terms" className="hover:text-white underline" data-testid="footer-terms-link">{t("Terms & Conditions · Privacy Policy")}</Link>
      </footer>
    </div>
  );
}
